import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { SURVEY_EDITOR, CHECKIN_ADMIN } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash, FloppyDisk, ShieldStar } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

// Which data-dictionary data_types are valid for each question_type
const QTYPE_TO_DTYPES = {
    'yes-no': ['yes-no'],
    'scale': ['scale', 'number'],
    'number': ['number'],
    'blood-pressure': [], // handled via bp_tag_enabled toggle (auto-pairs bp_systolic + bp_diastolic)
    'multiple-choice': ['choice', 'text'],
    'free-text': ['text'],
};

const emptyQ = () => ({
    step_id: 'stp_' + Math.random().toString(36).slice(2, 10),
    kind: 'question', content: '',
    question_type: 'yes-no', prompt: '', choices: [], flag_rule: null,
});

export default function CheckInEditor() {
    const { surveyId } = useParams();
    const { isAdmin } = useAuth();
    const [survey, setSurvey] = useState(null);
    const [saving, setSaving] = useState(false);
    const [concepts, setConcepts] = useState([]);

    useEffect(() => { api.get(`/surveys/${surveyId}`).then(r => setSurvey(r.data)); }, [surveyId]);
    useEffect(() => {
        if (!isAdmin) return;
        api.get('/admin/concepts').then(r => setConcepts(r.data)).catch(() => {});
    }, [isAdmin]);

    if (!survey) return <div className="p-10 text-sm text-slate-500">Loading…</div>;

    const updateStep = (i, patch) => {
        const steps = [...survey.steps];
        steps[i] = { ...steps[i], ...patch };
        setSurvey({ ...survey, steps });
    };
    const removeStep = (i) => setSurvey({ ...survey, steps: survey.steps.filter((_, idx) => idx !== i) });
    const addStep = () => setSurvey({ ...survey, steps: [...survey.steps, emptyQ()] });

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/surveys/${surveyId}`, { name: survey.name, description: survey.description, steps: survey.steps });
            toast.success('Saved');
        } catch { toast.error('Save failed'); }
        finally { setSaving(false); }
    };

    return (
        <div data-testid={SURVEY_EDITOR.root} className="px-6 md:px-10 py-8 max-w-4xl">
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                <Link to="/check-ins" className="hover:underline">Check-ins</Link>
            </div>
            <header className="mb-6 flex items-end justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <Input className="font-heading text-3xl md:text-4xl border-0 border-b border-transparent focus-visible:border-primary rounded-none px-0 h-auto py-2 bg-transparent"
                        value={survey.name} onChange={e => setSurvey({ ...survey, name: e.target.value })} />
                    <Textarea className="mt-2 border-0 focus-visible:ring-0 px-0 resize-none bg-transparent text-slate-600"
                        rows={2} placeholder="Description…"
                        value={survey.description || ''} onChange={e => setSurvey({ ...survey, description: e.target.value })} />
                </div>
                <Button data-testid={SURVEY_EDITOR.save} onClick={save} disabled={saving} className="rounded-full bg-primary text-primary-foreground">
                    <FloppyDisk size={16} className="mr-1" /> {saving ? 'Saving…' : 'Save'}
                </Button>
            </header>

            <div className="space-y-4">
                {survey.steps.map((s, i) => (
                    <div key={s.step_id} className="rounded-xl border border-slate-200 bg-card p-5 shadow-sm">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center">{i + 1}</div>
                            <Select value={s.kind} onValueChange={v => updateStep(i, { kind: v })}>
                                <SelectTrigger data-testid={SURVEY_EDITOR.stepKind} className="w-40"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="text">Text</SelectItem>
                                    <SelectItem value="video">Video link</SelectItem>
                                    <SelectItem value="question">Question</SelectItem>
                                </SelectContent>
                            </Select>
                            <Button size="icon" variant="ghost" data-testid={SURVEY_EDITOR.removeStep} onClick={() => removeStep(i)} className="ml-auto text-red-600 hover:bg-red-50"><Trash size={16} /></Button>
                        </div>
                        {s.kind !== 'question' ? (
                            <Textarea rows={s.kind === 'text' ? 3 : 1}
                                placeholder={s.kind === 'video' ? 'Video URL' : 'Message shown to your loved one…'}
                                value={s.content || ''} onChange={e => updateStep(i, { content: e.target.value })} />
                        ) : (
                            <div className="space-y-3">
                                <div><Label>Question</Label><Input data-testid={SURVEY_EDITOR.stepPrompt} value={s.prompt || ''} onChange={e => updateStep(i, { prompt: e.target.value })} /></div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Answer type</Label>
                                        <Select value={s.question_type} onValueChange={v => updateStep(i, { question_type: v, flag_rule: null, choices: v === 'multiple-choice' ? (s.choices || []) : [], bp_tag_enabled: v === 'blood-pressure' ? (s.bp_tag_enabled || false) : false })}>
                                            <SelectTrigger data-testid={SURVEY_EDITOR.stepQType}><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="yes-no">Yes / No</SelectItem>
                                                <SelectItem value="scale">Scale 0–10</SelectItem>
                                                <SelectItem value="number">Number</SelectItem>
                                                <SelectItem value="blood-pressure">Blood pressure</SelectItem>
                                                <SelectItem value="multiple-choice">Multiple choice</SelectItem>
                                                <SelectItem value="free-text">Free text</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {s.question_type === 'multiple-choice' && (
                                        <div>
                                            <Label>Choices (comma-separated)</Label>
                                            <Input data-testid={SURVEY_EDITOR.stepChoices}
                                                value={(s.choices || []).join(', ')}
                                                onChange={e => updateStep(i, { choices: e.target.value.split(',').map(x => x.trim()).filter(Boolean) })}
                                                placeholder="Great, Okay, Not so good" />
                                        </div>
                                    )}
                                    {s.question_type === 'number' && (
                                        <>
                                            <div>
                                                <Label>Unit label (shown to family)</Label>
                                                <Input data-testid={SURVEY_EDITOR.stepUnitLabel}
                                                    value={s.unit_label || ''}
                                                    onChange={e => updateStep(i, { unit_label: e.target.value })}
                                                    placeholder="lbs, mg/dL…" />
                                            </div>
                                            <div className="col-span-2 grid grid-cols-2 gap-3">
                                                <div>
                                                    <Label>Minimum (optional)</Label>
                                                    <Input data-testid={SURVEY_EDITOR.stepNumberMin} type="number" step="any"
                                                        value={s.number_min ?? ''}
                                                        onChange={e => updateStep(i, { number_min: e.target.value === '' ? null : Number(e.target.value) })} />
                                                </div>
                                                <div>
                                                    <Label>Maximum (optional)</Label>
                                                    <Input data-testid={SURVEY_EDITOR.stepNumberMax} type="number" step="any"
                                                        value={s.number_max ?? ''}
                                                        onChange={e => updateStep(i, { number_max: e.target.value === '' ? null : Number(e.target.value) })} />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    {s.question_type === 'blood-pressure' && (
                                        <div className="text-xs text-slate-500 pt-6">
                                            The family member will see two large boxes for the top and bottom numbers (like 120 / 80).
                                        </div>
                                    )}
                                </div>
                                {s.question_type && s.question_type !== 'free-text' && (
                                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                                        <div>
                                            <Label>When to flag</Label>
                                            {s.question_type === 'yes-no' && (
                                                <Select value={s.flag_rule?.value || 'none'}
                                                    onValueChange={v => updateStep(i, { flag_rule: v && v !== 'none' ? { op: 'yes-no', value: v } : null })}>
                                                    <SelectTrigger data-testid={SURVEY_EDITOR.stepFlagOp}><SelectValue placeholder="Never" /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="none">Never</SelectItem>
                                                        <SelectItem value="Yes">If Yes</SelectItem>
                                                        <SelectItem value="No">If No</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            )}
                                            {(s.question_type === 'scale' || s.question_type === 'number') && (
                                                <div className="flex items-center gap-2">
                                                    <Select value={s.flag_rule?.op || 'none'} onValueChange={op => updateStep(i, { flag_rule: op && op !== 'none' ? { op, value: s.flag_rule?.value ?? (s.question_type === 'scale' ? 7 : 0) } : null })}>
                                                        <SelectTrigger data-testid={SURVEY_EDITOR.stepFlagOp} className="w-28"><SelectValue placeholder="Never" /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="none">Never</SelectItem>
                                                            <SelectItem value=">=">≥</SelectItem>
                                                            <SelectItem value="<=">≤</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                    {s.flag_rule?.op && (
                                                        <Input data-testid={SURVEY_EDITOR.stepFlagValue} type="number" step="any" className="w-28"
                                                            {...(s.question_type === 'scale' ? { min: 0, max: 10 } : {})}
                                                            value={s.flag_rule?.value ?? 0}
                                                            onChange={e => updateStep(i, { flag_rule: { ...s.flag_rule, value: Number(e.target.value) } })} />
                                                    )}
                                                </div>
                                            )}
                                            {s.question_type === 'blood-pressure' && (
                                                <Select value={s.flag_rule?.op || 'none'}
                                                    onValueChange={op => updateStep(i, { flag_rule: op && op !== 'none' ? { op: 'bp-high', systolic_gte: 140, diastolic_gte: 90 } : null })}>
                                                    <SelectTrigger data-testid={SURVEY_EDITOR.stepFlagOp}><SelectValue placeholder="Never" /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="none">Never</SelectItem>
                                                        <SelectItem value="bp-high">If high (≥140/90)</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            )}
                                            {s.question_type === 'multiple-choice' && (
                                                <Input data-testid={SURVEY_EDITOR.stepFlagValue} placeholder="Answers that need attention"
                                                    value={(s.flag_rule?.values || []).join(', ')}
                                                    onChange={e => {
                                                        const values = e.target.value.split(',').map(x => x.trim()).filter(Boolean);
                                                        updateStep(i, { flag_rule: values.length ? { op: 'in', values } : null });
                                                    }} />
                                            )}
                                        </div>
                                        <div className="text-xs text-slate-500 pt-6">
                                            When the answer matches, this shows under <span className="font-semibold text-[#B45309]">Needs your attention</span> on your home page.
                                        </div>
                                    </div>
                                )}

                                {/* Branching: show only if a prior yes/no step was answered a certain way */}
                                {(() => {
                                    const priorYN = survey.steps.slice(0, i).filter(x => x.kind === 'question' && x.question_type === 'yes-no' && (x.prompt || '').trim());
                                    if (priorYN.length === 0) return null;
                                    const parentSid = s.show_if?.step_id || '';
                                    const parentVal = s.show_if?.value || '';
                                    return (
                                        <div className="grid grid-cols-[1fr_120px] gap-3 pt-2 border-t border-slate-100 items-end">
                                            <div>
                                                <Label>Only show this step if…</Label>
                                                <Select value={parentSid || 'none'}
                                                    onValueChange={sid => updateStep(i, { show_if: sid && sid !== 'none' ? { step_id: sid, value: parentVal || 'Yes' } : null })}>
                                                    <SelectTrigger data-testid="editor-showif-parent"><SelectValue placeholder="Always show" /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="none">Always show</SelectItem>
                                                        {priorYN.map(p => (
                                                            <SelectItem key={p.step_id} value={p.step_id}>&ldquo;{p.prompt.slice(0, 60)}&rdquo;</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            {parentSid && (
                                                <div>
                                                    <Label>answer is</Label>
                                                    <Select value={parentVal || 'Yes'}
                                                        onValueChange={v => updateStep(i, { show_if: { step_id: parentSid, value: v } })}>
                                                        <SelectTrigger data-testid="editor-showif-value"><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="Yes">Yes</SelectItem>
                                                            <SelectItem value="No">No</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                                {/* Admin-only: Data tag (concept) for question steps */}
                                {isAdmin && (() => {
                                    if (s.question_type === 'blood-pressure') {
                                        return (
                                            <div className="pt-3 mt-1 border-t border-dashed border-primary/30 bg-primary/[0.03] -mx-5 px-5 py-3">
                                                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-primary mb-2">
                                                    <ShieldStar size={11} weight="duotone" /> Admin only · Data tag
                                                </div>
                                                <label className="flex items-center gap-3 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        data-testid={SURVEY_EDITOR.stepBpTagToggle}
                                                        checked={!!s.bp_tag_enabled}
                                                        onChange={e => updateStep(i, { bp_tag_enabled: e.target.checked, concept_key: null })}
                                                        className="w-4 h-4 accent-primary"
                                                    />
                                                    <div className="text-sm text-slate-800">
                                                        Tag as blood pressure
                                                        <div className="text-[11px] text-slate-500 mt-0.5">
                                                            Automatically stores the top and bottom numbers as <span className="font-mono">bp_systolic</span> and <span className="font-mono">bp_diastolic</span>, and adds a Blood pressure reading to the member&apos;s profile.
                                                        </div>
                                                    </div>
                                                </label>
                                            </div>
                                        );
                                    }
                                    const allowed = QTYPE_TO_DTYPES[s.question_type] || [];
                                    const options = concepts
                                        .filter(c => c.status === 'Active' && allowed.includes(c.data_type))
                                        .sort((a, b) => a.display_name.localeCompare(b.display_name));
                                    return (
                                        <div className="pt-3 mt-1 border-t border-dashed border-primary/30 bg-primary/[0.03] -mx-5 px-5 py-3">
                                            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-primary mb-1">
                                                <ShieldStar size={11} weight="duotone" /> Admin only · Data tag
                                            </div>
                                            <Select
                                                value={s.concept_key || 'none'}
                                                onValueChange={v => updateStep(i, { concept_key: v && v !== 'none' ? v : null })}
                                            >
                                                <SelectTrigger data-testid={CHECKIN_ADMIN.conceptSelect}>
                                                    <SelectValue placeholder="No tag" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">No tag</SelectItem>
                                                    {options.map(c => (
                                                        <SelectItem key={c.key} value={c.key}>
                                                            {c.display_name}
                                                            <span className="text-slate-400"> · {c.key}</span>
                                                            {c.code && <span className="text-slate-400"> · {c.code_system} {c.code}</span>}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <div className="text-[11px] text-slate-500 mt-1">
                                                Optional. Answers to this question will be stored as structured data. Only affects future answers. Family members and coordinators never see this.
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}
                    </div>
                ))}
                <button data-testid={SURVEY_EDITOR.addStep} onClick={addStep}
                    className="w-full rounded-xl border-2 border-dashed border-slate-300 py-6 text-slate-600 hover:border-primary hover:text-primary transition-colors duration-150 flex items-center justify-center gap-2">
                    <Plus size={16} /> Add step
                </button>
            </div>
        </div>
    );
}
